import { useCallback } from 'react';
import { warn } from '../logger';
/**
 * Lytt på og send eventer fra og til webkomponenter
 * @param ref Ref til webkomponent
 * @param componentName Navn på webkomponent
 */
function useWebComponentEvents(componentName, ref) {
    /**
     * Finner host-elementet til webkomponenten (<hn-webcomp-name>)
     */
    const getHost = useCallback(() => {
        if (!ref.current) {
            return null;
        }
        const root = ref.current.getRootNode();
        if (root instanceof ShadowRoot) {
            return root.host;
        }
        return null;
    }, []);
    /**
     * Dispatch en custom event fra webkomponenten.
     */
    const dispatch = useCallback((eventName, detail) => {
        const host = getHost();
        if (host) {
            host.dispatchEvent(new CustomEvent(eventName, {
                bubbles: true,
                composed: true, // Åpner for at eventen kan krysse shadow DOM grenser
                detail,
            }));
        }
        else {
            warn(`Could not find host for ${componentName}. Did you attach the ref?`);
        }
    }, [getHost, componentName]);
    /**
     * Subscribe på eventer sendt til webkomponenten.
     */
    const subscribe = useCallback((eventName, callback) => {
        const host = getHost();
        if (!host) {
            return () => { };
        }
        // Sjekk for eksisterende events i global kø
        window.HN?.FrontendEvents?.forEach(item => {
            if (item.selector === componentName && item.event.type === eventName) {
                callback(item.event);
            }
        });
        const handleEvent = (e) => {
            if (e instanceof CustomEvent) {
                callback(e);
            }
            else {
                callback(e);
            }
        };
        host.addEventListener(eventName, handleEvent);
        // 4. Return Cleanup
        return () => host.removeEventListener(eventName, handleEvent);
    }, [getHost, componentName]);
    return { dispatch, subscribe };
}
export default useWebComponentEvents;
